import type { Lang } from '../../../i18n/publicEvents';

// SAM-633 — EN/ES copy for the public pay page. The language itself is chosen exactly like the
// other public pages (PublicLangProvider: ?lang= → localStorage → English), this file only holds
// the words.

const en = {
  pageTitle: 'Complete your payment',
  greeting: 'Hi {name}, your booking is being held for you.',
  greetingAnon: 'Your booking is being held for you.',
  holdEndsIn: 'Held for',
  holdHint: 'Pay before the timer runs out to keep this booking.',
  service: 'Service',
  date: 'Date',
  time: 'Time',
  guests: 'Guests',
  guestsCount: '{n} guest(s)',
  summary: 'Summary',
  addOn: 'Add-on',
  total: 'Total',
  payWithCard: 'Pay with card',
  secureNote: 'Your card is verified with your bank (3-D Secure). We never store your card details.',
  secureNoteNo3ds: 'We never store your card details.',
  payButton: 'Pay {amount}',
  keepOpen: 'Please don’t close or refresh this page.',
  payingTitle: 'Processing your payment',
  payingSub: 'This takes a few seconds.',
  payingSlow: 'This is taking a little longer than usual…',
  payingStepVerify: 'Verifying your card',
  payingStepCharge: 'Charging and confirming your booking',
  payingStepPass: 'Creating your pass',
  payingDoneTitle: 'All set!',
  payingDoneSub: 'Opening your receipt…',
  loading: 'Loading your booking…',

  unavailableTitle: 'This link is no longer available',
  unavailableBody: 'It may have expired, been replaced or already been used. Ask the business for a new payment link.',
  expiredTitle: 'Time ran out',
  expiredBody: 'The hold on this booking has ended and nothing was charged. Ask the business for a new payment link.',
  lockedTitle: 'Too many attempts',
  lockedBody: 'This payment link has been locked for your protection. Nothing more will be charged. Ask the business for a new link.',
  loadErrorTitle: 'We could not load this payment',
  loadErrorBody: 'Check your connection and try again.',
  tryAgain: 'Try again',

  processingTitle: 'Payment received — confirming',
  processingBody: 'Your payment went through and your booking is being confirmed. You will not be charged again. Do not pay a second time; you can refresh this page later to see your receipt.',
  pollingTitle: 'Checking your payment…',
  pollingBody: 'A payment for this booking is already in progress. We are checking its result — please do not pay again.',
  stillConfirmingBody: 'We are still confirming this payment. Do not pay again; refresh this page in a few minutes or contact the business.',

  paidTitle: 'Payment complete',
  paidBody: 'Your booking is confirmed. Keep this receipt for your records.',
  confirmationCode: 'Confirmation code',
  amountPaid: 'Amount paid',
  authCode: 'Authorization code',
  paidAt: 'Paid on',
  downloadReceipt: 'Download receipt',
  receiptFileName: 'Receipt',

  // Entry pass (paid state)
  passTitle: 'Your entry pass',
  passShowQr: 'Show this QR code at the entrance.',
  passValid: 'Valid',
  passGuests: 'Guests',
  passChildren: 'Children',
  passDoorPin: 'Door PIN',
  passDoorPinPending: 'Being issued — ask at the entrance',
  passBringId: 'Bring a photo ID. Staff may ask to see it at the entrance.',
  passSaveHint: 'Take a screenshot or open your pass link: this page stops showing the pass 24 hours after payment.',
  passOpen: 'Open my pass',
  passVerifyTitle: 'One more step: verify your identity',
  passVerifyBody: 'Before your pass can be used, confirm your phone number and scan your ID. Your QR code appears as soon as you finish.',
  passVerifyButton: 'Verify my identity',
  passUsedTitle: 'This pass has already been used',
  passUsedBody: 'It was checked out at the entrance. Contact the business if this is not right.',
  passCancelledTitle: 'This pass was cancelled',
  passCancelledBody: 'Contact the business about your booking.',
  passUnavailableTitle: 'Your pass is not ready yet',
  passUnavailableBody: 'Show your confirmation code at the entrance, or contact the business.',

  stoppedTitle: 'This payment could not be completed',

  // Error copy — one entry per backend error_code (payment_link.py), plus client-side cases.
  err_PAYMENT_DECLINED: 'Your card was declined. Nothing was charged. Try a different card.',
  err_attemptsRemaining: 'Attempts left on this link: {n}.',
  err_THREEDS_REQUIRED: 'Your bank verification did not complete. Nothing was charged — please try again.',
  err_THREEDS_MISMATCH: 'Your bank verification did not match this payment. Nothing was charged — please try again.',
  err_THREEDS_FAILED: 'Your bank could not verify this card. Nothing was charged — try again or use a different card.',
  err_THREEDS_TIMEOUT: 'Bank verification timed out. Nothing was charged — please try again.',
  err_CARD_REQUIRED: 'Enter your card number and expiration date.',
  err_CARDHOLDER_NAME_REQUIRED: 'Enter the name on the card.',
  err_PAYMENT_GATEWAY_ERROR: 'The payment could not be processed. Nothing was charged — please try again.',
  err_PAYMENT_TEMPORARILY_UNAVAILABLE: 'Payments for this booking are temporarily unavailable. Nothing was charged — please try again in a few minutes.',
  err_PAYMENT_SUPERSEDED: 'This payment attempt was cancelled and any authorization on your card is being reversed. You will not be charged for it. Contact the business to finish your booking.',
  err_business_closed: 'The business is closed on the date of this booking, so it cannot be paid. Nothing was charged. Contact the business.',
  err_NETWORK: 'Could not reach the payment server. Check your connection and try again.',
  err_GENERIC: 'Something went wrong. Nothing was charged — please try again.',
  err_previousAttempt: 'The previous attempt did not complete and nothing was charged. You can try again.',
  err_formIncomplete: 'Check the card details and try again.',
};

export type PayLinkStringKey = keyof typeof en;

const es: Record<PayLinkStringKey, string> = {
  pageTitle: 'Completa tu pago',
  greeting: 'Hola {name}, tu reserva está apartada para ti.',
  greetingAnon: 'Tu reserva está apartada para ti.',
  holdEndsIn: 'Apartada por',
  holdHint: 'Paga antes de que termine el tiempo para conservar esta reserva.',
  service: 'Servicio',
  date: 'Fecha',
  time: 'Hora',
  guests: 'Invitados',
  guestsCount: '{n} invitado(s)',
  summary: 'Resumen',
  addOn: 'Adicional',
  total: 'Total',
  payWithCard: 'Pagar con tarjeta',
  secureNote: 'Tu tarjeta se verifica con tu banco (3-D Secure). Nunca guardamos los datos de tu tarjeta.',
  secureNoteNo3ds: 'Nunca guardamos los datos de tu tarjeta.',
  payButton: 'Pagar {amount}',
  keepOpen: 'No cierres ni recargues esta página.',
  payingTitle: 'Procesando tu pago',
  payingSub: 'Toma unos segundos.',
  payingSlow: 'Esto está tardando un poco más de lo normal…',
  payingStepVerify: 'Verificando tu tarjeta',
  payingStepCharge: 'Cobrando y confirmando tu reserva',
  payingStepPass: 'Generando tu pase',
  payingDoneTitle: '¡Listo!',
  payingDoneSub: 'Abriendo tu recibo…',
  loading: 'Cargando tu reserva…',

  unavailableTitle: 'Este enlace ya no está disponible',
  unavailableBody: 'Puede que haya vencido, sido reemplazado o ya se haya usado. Pide al negocio un nuevo enlace de pago.',
  expiredTitle: 'Se acabó el tiempo',
  expiredBody: 'La reserva ya no está apartada y no se hizo ningún cargo. Pide al negocio un nuevo enlace de pago.',
  lockedTitle: 'Demasiados intentos',
  lockedBody: 'Este enlace de pago se bloqueó para tu protección. No se hará ningún otro cargo. Pide al negocio un nuevo enlace.',
  loadErrorTitle: 'No pudimos cargar este pago',
  loadErrorBody: 'Revisa tu conexión e inténtalo de nuevo.',
  tryAgain: 'Intentar de nuevo',

  processingTitle: 'Pago recibido — confirmando',
  processingBody: 'Tu pago se realizó y tu reserva se está confirmando. No se te cobrará de nuevo. No pagues otra vez; puedes recargar esta página más tarde para ver tu recibo.',
  pollingTitle: 'Verificando tu pago…',
  pollingBody: 'Ya hay un pago en curso para esta reserva. Estamos verificando el resultado — por favor no pagues otra vez.',
  stillConfirmingBody: 'Seguimos confirmando este pago. No pagues otra vez; recarga esta página en unos minutos o contacta al negocio.',

  paidTitle: 'Pago completado',
  paidBody: 'Tu reserva está confirmada. Guarda este recibo.',
  confirmationCode: 'Código de confirmación',
  amountPaid: 'Monto pagado',
  authCode: 'Código de autorización',
  paidAt: 'Pagado el',
  downloadReceipt: 'Descargar recibo',
  receiptFileName: 'Recibo',

  passTitle: 'Tu pase de entrada',
  passShowQr: 'Muestra este código QR en la entrada.',
  passValid: 'Válido',
  passGuests: 'Invitados',
  passChildren: 'Niños',
  passDoorPin: 'PIN de la puerta',
  passDoorPinPending: 'En proceso — pregunta en la entrada',
  passBringId: 'Lleva una identificación con foto. El personal puede pedírtela en la entrada.',
  passSaveHint: 'Toma una captura de pantalla o abre el enlace de tu pase: esta página deja de mostrarlo 24 horas después del pago.',
  passOpen: 'Abrir mi pase',
  passVerifyTitle: 'Un paso más: verifica tu identidad',
  passVerifyBody: 'Antes de usar tu pase, confirma tu número de teléfono y escanea tu identificación. Tu código QR aparece en cuanto termines.',
  passVerifyButton: 'Verificar mi identidad',
  passUsedTitle: 'Este pase ya se usó',
  passUsedBody: 'Se registró su salida en la entrada. Contacta al negocio si no es correcto.',
  passCancelledTitle: 'Este pase fue cancelado',
  passCancelledBody: 'Contacta al negocio sobre tu reserva.',
  passUnavailableTitle: 'Tu pase aún no está listo',
  passUnavailableBody: 'Muestra tu código de confirmación en la entrada o contacta al negocio.',

  stoppedTitle: 'Este pago no se pudo completar',

  err_PAYMENT_DECLINED: 'Tu tarjeta fue rechazada. No se hizo ningún cargo. Prueba con otra tarjeta.',
  err_attemptsRemaining: 'Intentos restantes en este enlace: {n}.',
  err_THREEDS_REQUIRED: 'La verificación de tu banco no se completó. No se hizo ningún cargo — inténtalo de nuevo.',
  err_THREEDS_MISMATCH: 'La verificación de tu banco no corresponde a este pago. No se hizo ningún cargo — inténtalo de nuevo.',
  err_THREEDS_FAILED: 'Tu banco no pudo verificar esta tarjeta. No se hizo ningún cargo — inténtalo de nuevo o usa otra tarjeta.',
  err_THREEDS_TIMEOUT: 'La verificación del banco tardó demasiado. No se hizo ningún cargo — inténtalo de nuevo.',
  err_CARD_REQUIRED: 'Ingresa el número de tu tarjeta y la fecha de vencimiento.',
  err_CARDHOLDER_NAME_REQUIRED: 'Ingresa el nombre que aparece en la tarjeta.',
  err_PAYMENT_GATEWAY_ERROR: 'No se pudo procesar el pago. No se hizo ningún cargo — inténtalo de nuevo.',
  err_PAYMENT_TEMPORARILY_UNAVAILABLE: 'Los pagos de esta reserva no están disponibles por el momento. No se hizo ningún cargo — inténtalo de nuevo en unos minutos.',
  err_PAYMENT_SUPERSEDED: 'Este intento de pago se canceló y cualquier autorización en tu tarjeta se está revirtiendo. No se te cobrará. Contacta al negocio para completar tu reserva.',
  err_business_closed: 'El negocio está cerrado en la fecha de esta reserva, así que no se puede pagar. No se hizo ningún cargo. Contacta al negocio.',
  err_NETWORK: 'No pudimos conectar con el servidor de pagos. Revisa tu conexión e inténtalo de nuevo.',
  err_GENERIC: 'Algo salió mal. No se hizo ningún cargo — inténtalo de nuevo.',
  err_previousAttempt: 'El intento anterior no se completó y no se hizo ningún cargo. Puedes intentarlo de nuevo.',
  err_formIncomplete: 'Revisa los datos de la tarjeta e inténtalo de nuevo.',
};

const STRINGS: Record<Lang, Record<PayLinkStringKey, string>> = { en, es };

export function payLinkT(lang: Lang, key: PayLinkStringKey, vars?: Record<string, string | number>): string {
  let text = STRINGS[lang]?.[key] ?? en[key] ?? String(key);
  if (vars) {
    for (const [name, value] of Object.entries(vars)) text = text.split(`{${name}}`).join(String(value));
  }
  return text;
}

/** Card-form labels and validation messages handed to the shared CreditCardInput. */
export const CARD_FORM_LABELS: Record<Lang, {
  cardNumber: string; cardHolder: string; expiryDate: string; cvv: string;
  required: string; invalidCardNumber: string; invalidExpiry: string; expired: string; invalidCvv: string; invalidName: string;
  brandNotAccepted: string;
}> = {
  en: {
    cardNumber: 'Card number', cardHolder: 'Name on card', expiryDate: 'Expiry (MM/YY)', cvv: 'CVV',
    required: 'Required', invalidCardNumber: 'Card number must be 16 digits', invalidExpiry: 'Use MM/YY',
    expired: 'Card has expired', invalidCvv: 'CVV must be 3 or 4 digits', invalidName: 'Enter the name on the card',
    brandNotAccepted: 'This card brand is not accepted. Use another card.',
  },
  es: {
    cardNumber: 'Número de tarjeta', cardHolder: 'Nombre en la tarjeta', expiryDate: 'Vence (MM/AA)', cvv: 'CVV',
    required: 'Requerido', invalidCardNumber: 'El número debe tener 16 dígitos', invalidExpiry: 'Usa MM/AA',
    expired: 'La tarjeta está vencida', invalidCvv: 'El CVV debe tener 3 o 4 dígitos', invalidName: 'Ingresa el nombre de la tarjeta',
    brandNotAccepted: 'No aceptamos esta marca de tarjeta. Usa otra.',
  },
};
